<template>
  <div>
    <section class="rate" :class="{ shadow }">
      <header class="header">
        <AppImage
          v-if="showCompany && company"
          class="logo"
          contain
          background-color="#fff"
          border-radius="15%"
          :src="company.image.thumbnail"
        />

        <strong class="title text-uppercase"
          >Como foi sua última experiência{{
            showCompany && company ? ` com ${company.name}` : ''
          }}?</strong
        >

        <AppButton
          v-if="showCloseButton"
          @click="$emit('close')"
          class="close-button"
          noShadow
          small
          icon
          basic
        >
          <AppIcon>close</AppIcon>
        </AppButton>
      </header>

      <div class="actions" :class="{ disabled: showWarning }">
        <CompanyRatingBadge
          @click="showRateModal(COMPANY_RATING_OPTION_LOVED)"
          loved
          :opaque="
            userRating?.id &&
            userRating?.ratingOption !== COMPANY_RATING_OPTION_LOVED
          "
        />

        <CompanyRatingBadge
          @click="showRateModal(COMPANY_RATING_OPTION_LIKED)"
          liked
          :opaque="
            userRating?.id &&
            userRating?.ratingOption !== COMPANY_RATING_OPTION_LIKED
          "
        />

        <CompanyRatingBadge
          @click="showRateModal(COMPANY_RATING_OPTION_NOT_LIKED)"
          notLiked
          :opaque="
            userRating?.id &&
            userRating?.ratingOption !== COMPANY_RATING_OPTION_NOT_LIKED
          "
        />
      </div>

      <span v-if="showWarning" class="warning muted">
        <AppImage class="icon" src="/images/icons/feedback2.svg" />

        <template v-if="!userId">
          Você precisa estar logado para avaliar esta empresa.
        </template>

        <template v-else-if="userHasOrderInCompany === false">
          Você precisa fazer um pedido para avaliar esta empresa.
        </template>
      </span>
    </section>

    <AppModal ref="rateModal" showCloseButton>
      <template #header> Como foi sua última experiência? </template>

      <template #content>
        <CompanyRatingBadge
          v-if="userRating"
          :option="userRating.ratingOption"
        />

        <AppForm id="rateForm" ref="rateForm" @submit="checkForm">
          <AppInput
            v-if="userRating"
            v-model="userRating.comment"
            ref="comment"
            type="textarea"
            :placeholder="
              isRequiredComment
                ? 'O que aconteceu? (obrigatório)'
                : 'Conta um pouco mais pra galera (opcional)'
            "
            :required="isRequiredComment"
          />
        </AppForm>
      </template>

      <template #footer>
        <AppButton
          type="submit"
          form="rateForm"
          primary
          fullWidth
          :disabled="$refs.rateForm?.disable"
          :loading="$refs.rateForm?.disable"
          >Enviar</AppButton
        >
      </template>
    </AppModal>
  </div>
</template>

<script>
import CompanyRatingBadge from '@/components/Company/Rating/Badge.vue'
import AppImage from '@/components/App/Image.vue'
import AppInput from '@/components/App/Input.vue'
import AppModal from '@/components/App/Modal.vue'
import AppButton from '@/components/App/Buttons/Button.vue'
import AppIcon from '@/components/App/Icon.vue'
import AppForm from '@/components/App/Form.vue'
import {
  COMPANY_RATING_OPTION_NOT_LIKED,
  COMPANY_RATING_OPTION_LIKED,
  COMPANY_RATING_OPTION_LOVED,
} from '@/constants'
import { clone, fullTrim } from '@/helpers'

async function watchUser(value, oldValue) {
  const { userId, companyId } = this

  if (value === oldValue || !userId || !companyId) return

  await Promise.all([this.loadUserRating(), this.loadUserHasOrderInCompany()])
}

export default {
  name: 'CompanyRatingRate',

  components: {
    CompanyRatingBadge,
    AppImage,
    AppInput,
    AppModal,
    AppButton,
    AppIcon,
    AppForm,
  },

  data() {
    this.COMPANY_RATING_OPTION_NOT_LIKED = COMPANY_RATING_OPTION_NOT_LIKED
    this.COMPANY_RATING_OPTION_LIKED = COMPANY_RATING_OPTION_LIKED
    this.COMPANY_RATING_OPTION_LOVED = COMPANY_RATING_OPTION_LOVED

    return {
      userHasOrderInCompany: undefined,
      userRating: {
        id: undefined,
        ratingOption: undefined,
        comment: undefined,
      },
    }
  },

  props: {
    company: {
      type: Object,
      required: true,
    },

    showCompany: {
      type: Boolean,
      default: true,
    },

    showCloseButton: {
      type: Boolean,
      default: false,
    },

    shadow: {
      type: Boolean,
      default: false,
    },

    dontLoadUserRating: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isRequiredComment() {
      return this.userRating?.ratingOption === COMPANY_RATING_OPTION_NOT_LIKED
    },

    userId() {
      return app.user?.id ?? this.$attrs.userId
    },

    companyId() {
      return this.company?.id ?? this.$attrs.companyId
    },

    showWarning() {
      return this.userHasOrderInCompany === false || !this.userId
    },
  },

  watch: {
    userId: {
      immediate: true,
      handler: watchUser,
    },

    companyId: {
      immediate: true,
      handler: watchUser,
    },
  },

  methods: {
    async loadUserHasOrderInCompany({ userId, companyId } = this) {
      let result = false

      if (userId) {
        const { total: totalOrders } = await this.getOrders({
          companyId,
          userId,
          isValid: true,
          fields: 'id',
          limit: 1,
        })

        result = totalOrders > 0
      }

      this.userHasOrderInCompany = result

      return result
    },

    async loadUserRating({ userId, companyId, dontLoadUserRating } = this) {
      if (dontLoadUserRating) return

      let { userRating } = this.$attrs

      if (!userRating && userId) {
        const { data = [] } = await this.getCompanyRating({
          companyId,
          userId,
          limit: 1,
        })

        userRating = data?.[0]
      }

      if (!userRating) userRating = this.$options.data().userRating

      this.userRating = userRating

      return userRating
    },

    async checkForm(form) {
      try {
        form.disable = true

        const { companyId, userRating, isRequiredComment } = this
        const { ratingOption } = userRating

        let { comment } = userRating

        if (isRequiredComment) {
          comment = fullTrim(comment)

          if (!comment) {
            await app.alert({
              title: 'Opss...',
              content: 'Por favor, descreva o que aconteceu.',
            })

            this.$refs.comment?.focus?.()

            return
          } else if (comment.includes(' ') === false || comment.length <= 5) {
            await app.alert({
              title: 'Opss...',
              content: 'Por favor, descreva melhor o que aconteceu.',
            })

            this.$refs.comment?.focus?.()

            return
          }
        }

        const newUserRating = await app.callApi({
          method: 'POST',
          action: 'companies/ratings',
          version: 1,
          data: {
            companyId,
            ratingOption,
            comment,
          },
        })

        this.userRating = newUserRating

        await app.alert({
          title: 'Tudo certo...',
          content: comment
            ? 'Seu comentário será analisado antes da aprovação.'
            : 'Agradecemos a sua avaliação.',
          onClosed: async () => {
            await this.$refs.rateModal.close()

            this.$emit('rated', clone(newUserRating))
          },
        })
      } finally {
        form.disable = false
      }
    },

    async getOrders(query = {}) {
      return app.callApi({
        method: 'GET',
        action: 'orders',
        version: 1,
        query,
      })
    },

    async getCompanyRating(query = {}) {
      return app.callApi({
        method: 'GET',
        action: 'companies/ratings',
        version: 1,
        query,
      })
    },

    async showRateModal(ratingOption) {
      const { companyId } = this

      let { userRating } = this

      if (!userRating)
        userRating = this.userRating = {
          companyId,
          ratingOption: null,
          comment: null,
        }

      userRating.ratingOption = ratingOption

      await this.$nextTick()

      return this.$refs.rateModal.open()
    },
  },
}
</script>

<style lang="scss" scoped>
.rate {
  background-color: rgba(255, 245, 233, 1);
  padding: 1rem;
  border-radius: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.8rem;
  line-height: 1.25;

  &.shadow {
    box-shadow: 0px 12px 20px 0px rgba(0, 0, 0, 0.32);
  }

  .muted {
    color: rgba(102, 102, 102, 1);
  }

  .header {
    display: flex;
    gap: 0.5rem;
    align-items: center;

    .logo {
      $size: 2rem;
      width: $size;
      height: $size;
    }

    .title {
      flex: 1;
    }

    .close-button {
      align-self: flex-start;
      padding: 0;
      background-color: transparent;
      min-height: auto;
      box-shadow: none !important;
      border-radius: 0;
      border: 0;
      font-size: 1.5rem;
    }
  }

  .actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }

  .warning {
    margin-top: 0.5rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;

    .icon {
      $size: 1.5rem;
      width: $size;
      height: $size;
    }
  }
}
</style>
